import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Stack,
  Button,
  Table,
  Badge,
  TextInput,
  Select,
  Modal,
  Textarea,
  Loader,
  ActionIcon,
  Grid,
  Divider,
  SimpleGrid,
} from "@mantine/core";
import {
  IconSearch,
  IconRefresh,
  IconFileText,
  IconDotsVertical,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconEye,
  IconAlertCircle,
  IconDownload,
  IconExternalLink,
} from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";

const API_BASE_URL = "https://admin-dashboard-backend-sbu4.onrender.com";

type MOUStatus =
  | "Sent"
  | "Viewed"
  | "Signed"
  | "IN PROGRESS"
  | "RECALLED"
  | "Declined"
  | "Expired"
  | "Voided";

interface MOURecord {
  requestId: string;
  trainerName?: string;
  trainerEmail: string;
  courseBatch: string;
  status: MOUStatus;
  createdAt: string;
  mouVersion?: number;
  numOfStud?: string;
}

interface SendMOURequest {
  templateId: string;
  trainerActionId: string;
  trainerName: string;
  trainerEmail: string;
  courseBatch: string;
  mouAmount: string;
  mouHours?: string;
  numOfStud?: string;
  paymentTermsText: string;
  mouSignDate: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalFiltered: number;
  hasMore: boolean;
  lastEvaluatedKey: string | null;
}

interface StatsInfo {
  total: number;
  signed: number;
  inProgress: number;
  recalled: number;
}

const statusColor: Record<MOUStatus, string> = {
  Sent: "blue",
  Viewed: "violet",
  Signed: "green",
  "IN PROGRESS": "yellow",
  RECALLED: "red",
  Declined: "gray",
  Expired: "orange",
  Voided: "gray",
};

export default function AllMou() {
  const [mous, setMous] = useState<MOURecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [recallLoading, setRecallLoading] = useState<string | null>(null);
  const [confirmRecall, setConfirmRecall] = useState<MOURecord | null>(null);
  
  // Debounce search to avoid too many API calls
  const [debouncedSearch] = useDebouncedValue(search, 500);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    totalFiltered: 0,
    hasMore: false,
    lastEvaluatedKey: null,
  });

  // Stats state
  const [stats, setStats] = useState<StatsInfo>({
    total: 0,
    signed: 0,
    inProgress: 0,
    recalled: 0,
  });

  const [form, setForm] = useState<SendMOURequest>({
    templateId: "",
    trainerActionId: "",
    trainerName: "",
    trainerEmail: "",
    courseBatch: "",
    mouAmount: "",
    mouHours: "",
    numOfStud: "",
    paymentTermsText: "",
    mouSignDate: new Date().toISOString().split("T")[0],
  });

  // Fetch MOUs when component mounts or filters change
  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchMOUs(1);
  }, [debouncedSearch, statusFilter]);

  const fetchMOUs = async (page: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "20",
      });
      
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const res = await fetch(`${API_BASE_URL}/mou/all?${params.toString()}`);
      const data = await res.json();
      
      if (data.success) {
        setMous(data.data);
        setPagination(data.pagination);
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch MOUs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchMOUs(page);
  };

  const sendMOU = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/mou/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOpen(false);
        // Reset to page 1 and fetch
        setCurrentPage(1);
        fetchMOUs(1);
        // Reset form
        setForm({
          templateId: "",
          trainerActionId: "",
          trainerName: "",
          trainerEmail: "",
          courseBatch: "",
          mouAmount: "",
          mouHours: "",
          numOfStud: "",
          paymentTermsText: "",
          mouSignDate: new Date().toISOString().split("T")[0],
        });
      }
    } catch (error) {
      console.error("Failed to send MOU:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchMOUs(1);
  };

  const handleRecallMOU = async (mou: MOURecord) => {
    setRecallLoading(mou.requestId);
    try {
      const response = await fetch(`${API_BASE_URL}/mou/recall/${mou.requestId}`, {
        method: "DELETE",
      });
      
      const data = await response.json();
      
      if (data.success) {
        notifications.show({
          title: "MOU Recalled",
          message: `MOU for ${mou.courseBatch} has been successfully recalled.`,
          color: "green",
        });
        
        // Refresh the list
        fetchMOUs(currentPage);
      } else {
        notifications.show({
          title: "Recall Failed",
          message: data.error || "Failed to recall MOU",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Failed to recall MOU:", error);
      notifications.show({
        title: "Error",
        message: "An error occurred while recalling the MOU",
        color: "red",
      });
    } finally {
      setRecallLoading(null);
      setConfirmRecall(null);
    }
  };

  const canRecall = (status: MOUStatus) => {
    // Only allow recall for MOUs that are Sent, Viewed, or In Progress
    return ["Sent", "Viewed", "IN PROGRESS"].includes(status);
  };

  const handleViewDocument = (requestId: string) => {
    // Open the PDF in a new tab directly
    const pdfUrl = `${API_BASE_URL}/mou/document/${requestId}/pdf`;
    window.open(pdfUrl, '_blank');
    
    notifications.show({
      title: "Opening Document",
      message: "MOU document is opening in a new tab",
      color: "blue",
      autoClose: 2000,
    });
  };

  const handleDownloadDocument = async (requestId: string, courseBatch: string) => {
    try {
      notifications.show({
        title: "Downloading...",
        message: "Your download will start shortly",
        color: "blue",
        autoClose: 2000,
      });

      const response = await fetch(`${API_BASE_URL}/mou/document/${requestId}/download`);
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        // Check if it's a PDF
        if (contentType && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `MOU_${courseBatch}_${requestId}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          notifications.show({
            title: "Download Complete",
            message: "MOU document has been downloaded",
            color: "green",
          });
        } else {
          // If it's JSON error response
          const data = await response.json();
          notifications.show({
            title: "Download Not Available",
            message: data.message || "Document may not be ready yet",
            color: "orange",
            autoClose: 5000,
          });
        }
      } else {
        const errorData = await response.json();
        notifications.show({
          title: "Download Failed",
          message: errorData.message || "Failed to download document",
          color: "red",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Failed to download document:", error);
      notifications.show({
        title: "Error",
        message: "An error occurred while downloading",
        color: "red",
      });
    }
  };

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>MOU Management</Title>
          <Text c="dimmed">Track & manage trainer MOUs</Text>
        </div>
      </Group>

      {/* Filters */}
      <Paper withBorder p="md" radius="md" mb="md">
        <Group grow>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder="Search trainer, email, batch"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            description={debouncedSearch !== search ? "Searching..." : null}
          />
          <Select
            data={[
              { value: "all", label: "All Status" },
              { value: "Sent", label: "Sent" },
              { value: "Viewed", label: "Viewed" },
              { value: "Signed", label: "Signed" },
              { value: "IN PROGRESS", label: "In Progress" },
              { value: "RECALLED", label: "Recalled" },
              { value: "Declined", label: "Declined" },
              { value: "Expired", label: "Expired" },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v || "all")}
          />
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" mb="md">
        <Paper withBorder p="md" radius="md">
          <Text size="sm" c="dimmed">
            Total MOUs
          </Text>
          <Title order={3}>{stats.total}</Title>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="sm" c="dimmed">
            Signed
          </Text>
          <Title order={3} c="green">
            {stats.signed}
          </Title>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="sm" c="dimmed">
            In Progress
          </Text>
          <Title order={3} c="yellow">
            {stats.inProgress}
          </Title>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Text size="sm" c="dimmed">
            Recalled
          </Text>
          <Title order={3} c="red">
            {stats.recalled}
          </Title>
        </Paper>
      </SimpleGrid>

      {/* Table */}
      <Paper withBorder radius="md" mb="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Document</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Batch</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th>Students</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={8} align="center">
                  <Group justify="center" p="xl">
                    <Loader />
                    <Text c="dimmed">Loading MOUs...</Text>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ) : mous.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8} align="center">
                  <Stack align="center" gap="xs" py="xl">
                    <IconFileText size={48} stroke={1} color="gray" />
                    <Text c="dimmed">No MOUs found</Text>
                    {(search || statusFilter !== "all") && (
                      <Text size="sm" c="dimmed">
                        Try adjusting your search or filters
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ) : (
              mous.map((m) => (
                <Table.Tr key={m.requestId}>
                  <Table.Td>
                    <Group gap="xs">
                      <IconFileText size={18} />
                      <Text size="sm" fw={500}>
                        MOU {m.courseBatch}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{m.trainerEmail}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{m.courseBatch}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm">
                      v{m.mouVersion || 1}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{m.numOfStud || "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusColor[m.status]}>{m.status}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {new Date(m.createdAt).toLocaleDateString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon 
                          variant="subtle"
                          loading={recallLoading === m.requestId}
                        >
                          <IconDotsVertical size={18} />
                        </ActionIcon>
                      </Menu.Target>

                      <Menu.Dropdown>
                        <Menu.Label>MOU Actions</Menu.Label>
                        <Menu.Item
                          leftSection={<IconEye size={16} />}
                          onClick={() => handleViewDocument(m.requestId)}
                        >
                          View Document
                        </Menu.Item>
                        
                        <Menu.Item
                          leftSection={<IconDownload size={16} />}
                          onClick={() => handleDownloadDocument(m.requestId, m.courseBatch)}
                        >
                          Download PDF
                        </Menu.Item>
                        
                        <Menu.Divider />
                        
                        <Menu.Item
                          color="red"
                          leftSection={<IconX size={16} />}
                          onClick={() => setConfirmRecall(m)}
                          disabled={!canRecall(m.status)}
                        >
                          Recall MOU
                        </Menu.Item>
                        
                        {!canRecall(m.status) && (
                          <Menu.Label>
                            <Text size="xs" c="dimmed">
                              Cannot recall {m.status} MOU
                            </Text>
                          </Menu.Label>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>

        {/* Pagination Info */}
        {!loading && mous.length > 0 && (
          <Group justify="space-between" p="md" style={{ borderTop: "1px solid #e9ecef" }}>
            <Text size="sm" c="dimmed">
              Showing {mous.length} MOUs on page {currentPage}
              {pagination.totalFiltered && ` (${pagination.totalFiltered} total)`}
            </Text>
            <Group gap="xs">
              <Button
                variant="default"
                size="sm"
                leftSection={<IconChevronLeft size={16} />}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </Button>
              <Badge variant="light" size="lg">
                Page {currentPage}
              </Badge>
              <Button
                variant="default"
                size="sm"
                rightSection={<IconChevronRight size={16} />}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasMore || loading}
              >
                Next
              </Button>
            </Group>
          </Group>
        )}
      </Paper>

      {/* Send Modal */}
      <Modal opened={open} onClose={() => setOpen(false)} title="Send New MOU" size="lg">
        <Stack>
          <Grid>
            <Grid.Col span={6}>
              <TextInput 
                label="Template ID" 
                required 
                value={form.templateId}
                onChange={(e) => setForm({ ...form, templateId: e.target.value })} 
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput 
                label="Trainer Action ID" 
                required 
                value={form.trainerActionId}
                onChange={(e) => setForm({ ...form, trainerActionId: e.target.value })} 
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <TextInput 
                label="Trainer Name" 
                required 
                value={form.trainerName}
                onChange={(e) => setForm({ ...form, trainerName: e.target.value })} 
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput 
                label="Trainer Email" 
                required 
                value={form.trainerEmail}
                onChange={(e) => setForm({ ...form, trainerEmail: e.target.value })} 
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <TextInput 
                label="Course Batch" 
                required 
                value={form.courseBatch}
                onChange={(e) => setForm({ ...form, courseBatch: e.target.value })} 
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput 
                label="MOU Amount" 
                required 
                value={form.mouAmount}
                onChange={(e) => setForm({ ...form, mouAmount: e.target.value })} 
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <TextInput 
                label="Number of Students" 
                required
                value={form.numOfStud}
                onChange={(e) => setForm({ ...form, numOfStud: e.target.value })} 
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput 
                label="MOU Hours" 
                value={form.mouHours}
                onChange={(e) => setForm({ ...form, mouHours: e.target.value })} 
              />
            </Grid.Col>
          </Grid>

          <Textarea
            label="Payment Terms"
            required
            value={form.paymentTermsText}
            onChange={(e) => setForm({ ...form, paymentTermsText: e.target.value })}
            minRows={3}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button loading={loading} onClick={sendMOU}>
              Send MOU
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Recall Confirmation Modal */}
      <Modal
        opened={!!confirmRecall}
        onClose={() => setConfirmRecall(null)}
        title="Confirm MOU Recall"
        centered
      >
        <Stack>
          <Group>
            <IconAlertCircle size={24} color="orange" />
            <Text size="sm" fw={500}>
              Are you sure you want to recall this MOU?
            </Text>
          </Group>

          {confirmRecall && (
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group justify="apart">
                  <Text size="sm" c="dimmed">Batch:</Text>
                  <Badge>{confirmRecall.courseBatch}</Badge>
                </Group>
                <Group justify="apart">
                  <Text size="sm" c="dimmed">Email:</Text>
                  <Text size="sm">{confirmRecall.trainerEmail}</Text>
                </Group>
                <Group justify="apart">
                  <Text size="sm" c="dimmed">Status:</Text>
                  <Badge color={statusColor[confirmRecall.status]}>
                    {confirmRecall.status}
                  </Badge>
                </Group>
              </Stack>
            </Paper>
          )}

          <Text size="sm" c="dimmed">
            This action cannot be undone. The trainer will no longer be able to sign this MOU.
          </Text>

          <Divider />

          <Group justify="flex-end">
            <Button 
              variant="default" 
              onClick={() => setConfirmRecall(null)}
              disabled={!!recallLoading}
            >
              Cancel
            </Button>
            <Button 
              color="red" 
              loading={!!recallLoading}
              onClick={() => confirmRecall && handleRecallMOU(confirmRecall)}
            >
              Recall MOU
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}